/**
 * src/admin-ui/src/pages/ModelUsageAnalytics.tsx
 *
 * Administrator analytics over model usage, built on the shared
 * full-screen table pattern (components/edge-table): EdgeDataTable with
 * per-column Excel-like filters over a single full aggregate fetch, and
 * page-specific bottom-bar tiles for the date range and grouping.
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Chip,
  IconButton,
  Snackbar,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import { ContentCopyOutlined } from "@mui/icons-material";
import {
  BillingStatus,
  GroupByDimension,
  UsageGroup,
  getUsageAggregate,
} from "../services/usageAnalytics";
import { formatBalls } from "../utils/format";
import EdgeDataTable, {
  EdgeColumn,
} from "../components/edge-table/EdgeDataTable";
import { BarTile } from "../components/edge-table/EdgeTableLayout.styles";
import { FilterableCell } from "../utils/usageTableFilters";
import { copyToClipboard } from "../utils/clipboard";

const STATUS_LABELS: Record<BillingStatus, string> = {
  billed: "Тарифицировано",
  unidentified: "Модель не распознана",
  no_units: "Нет единиц потребления",
  not_in_catalog: "Нет идентификатора в каталоге",
  unpriced: "Нет ценника",
};

const DIMENSION_LABELS: Record<GroupByDimension, string> = {
  user: "Пользователь",
  api_key: "Ключ",
  provider: "Провайдер",
  model: "Модель",
  billing_status: "Статус",
};

const METRIC_COLUMNS: EdgeColumn<UsageGroup>[] = [
  {
    id: "requests_count",
    label: "Запросы",
    filterKind: "number",
    numeric: true,
  },
  {
    id: "billed_requests_count",
    label: "Тарифицировано",
    filterKind: "number",
    numeric: true,
  },
  { id: "balls_spent", label: "Баллы", filterKind: "number", numeric: true },
  {
    id: "input_tokens",
    label: "Токены (вход)",
    filterKind: "number",
    numeric: true,
  },
  {
    id: "output_tokens",
    label: "Токены (выход)",
    filterKind: "number",
    numeric: true,
  },
  {
    id: "unique_users",
    label: "Пользователи",
    filterKind: "number",
    numeric: true,
  },
  {
    id: "unique_api_keys",
    label: "Ключи",
    filterKind: "number",
    numeric: true,
  },
  { id: "last_seen", label: "Последнее использование", filterKind: "text" },
];

const DAY_MS = 24 * 60 * 60 * 1000;
const FETCH_LIMIT = 10000;
const PAGE_SIZE = 100;

const toUtcDateString = (date: Date): string => date.toISOString().slice(0, 10);

const formatNumber = (value: number): string => value.toLocaleString("ru-RU");

const formatSeen = (seconds: number | null): string =>
  seconds === null ? "—" : new Date(seconds * 1000).toLocaleString("ru-RU");

const dimensionText = (item: UsageGroup, dim: GroupByDimension): string => {
  switch (dim) {
    case "user":
      return item.user_email || item.user_name || `ID ${item.user_id}`;
    case "api_key":
      return item.api_key_masked || "—";
    case "provider":
      return item.provider || "—";
    case "model":
      return item.model_name || "—";
    case "billing_status":
      return item.billing_status ? STATUS_LABELS[item.billing_status] : "—";
    default:
      return "—";
  }
};

const getCell = (row: UsageGroup, columnId: string): FilterableCell => {
  switch (columnId) {
    case "user":
    case "api_key":
    case "provider":
    case "model":
    case "billing_status":
      return { text: dimensionText(row, columnId) };
    case "requests_count":
      return {
        num: row.requests_count,
        text: formatNumber(row.requests_count),
      };
    case "billed_requests_count":
      return {
        num: row.billed_requests_count,
        text: formatNumber(row.billed_requests_count),
      };
    case "balls_spent": {
      const num = Number(row.balls_spent);
      return { num, text: formatBalls(num) };
    }
    case "input_tokens":
      return { num: row.input_tokens, text: formatNumber(row.input_tokens) };
    case "output_tokens":
      return { num: row.output_tokens, text: formatNumber(row.output_tokens) };
    case "unique_users":
      return { num: row.unique_users, text: formatNumber(row.unique_users) };
    case "unique_api_keys":
      return {
        num: row.unique_api_keys,
        text: formatNumber(row.unique_api_keys),
      };
    case "last_seen":
      return { text: formatSeen(row.last_seen) };
    default:
      return { text: "—" };
  }
};

const ModelUsageAnalytics: React.FC = () => {
  const [fromDate, setFromDate] = useState(
    toUtcDateString(new Date(Date.now() - 29 * DAY_MS)),
  );
  const [toDate, setToDate] = useState(toUtcDateString(new Date()));
  const [groupBy, setGroupBy] = useState<GroupByDimension[]>([
    "provider",
    "model",
  ]);
  const [rows, setRows] = useState<UsageGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState<{
    message: string;
    severity: "success" | "error" | "info";
  } | null>(null);

  // Отчётный период в UTC: from включительно, to исключительно (конец дня to).
  const period = useMemo(() => {
    const exclusiveTo = new Date(
      new Date(`${toDate}T00:00:00Z`).getTime() + DAY_MS,
    );
    return {
      periodFrom: `${fromDate}T00:00:00`,
      periodTo: `${toUtcDateString(exclusiveTo)}T00:00:00`,
    };
  }, [fromDate, toDate]);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getUsageAggregate(period, groupBy, 0, FETCH_LIMIT);
      setRows(data.items);
      if (data.total > data.items.length) {
        setToast({
          message: `Показаны первые ${formatNumber(data.items.length)} групп из ${formatNumber(data.total)} — сузьте период`,
          severity: "info",
        });
      }
    } catch (error) {
      console.error("Failed to load usage analytics", error);
      setToast({
        message: "Не удалось загрузить аналитику использования",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  }, [period, groupBy]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const columns = useMemo<EdgeColumn<UsageGroup>[]>(
    () => [
      ...groupBy.map((dim) => ({
        id: dim,
        label: DIMENSION_LABELS[dim],
        filterKind: "text" as const,
      })),
      ...METRIC_COLUMNS,
    ],
    [groupBy],
  );

  const handleGroupByChange = (_: unknown, value: GroupByDimension[]) => {
    if (value.length === 0) return;
    setGroupBy(value);
  };

  return (
    <>
      <EdgeDataTable
        ariaLabel="Агрегаты использования моделей"
        columns={columns}
        rows={rows}
        getCell={getCell}
        rowKey={(_, index) => index}
        loading={loading}
        emptyMessage="Нет использований за выбранный период и фильтры."
        clientPageSize={PAGE_SIZE}
        renderMobileCard={(item) => (
          <>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography variant="subtitle1" sx={{ flex: 1 }}>
                {groupBy.map((dim) => dimensionText(item, dim)).join(" · ")}
              </Typography>
              {groupBy.includes("model") && item.model_name && (
                <Tooltip title="Скопировать идентификатор модели">
                  <IconButton
                    size="small"
                    onClick={async () => {
                      const success = await copyToClipboard(item.model_name!);
                      setToast({
                        message: success
                          ? "Скопировано в буфер обмена"
                          : "Ошибка копирования",
                        severity: success ? "success" : "error",
                      });
                    }}
                  >
                    <ContentCopyOutlined sx={{ fontSize: 18 }} />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
            <Typography variant="body2" color="text.secondary">
              {formatNumber(item.requests_count)} запросов ·{" "}
              {formatBalls(Number(item.balls_spent))}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Токены: {formatNumber(item.input_tokens)} /{" "}
              {formatNumber(item.output_tokens)} · Последнее:{" "}
              {formatSeen(item.last_seen)}
            </Typography>
            {item.billing_status && (
              <Chip
                size="small"
                label={STATUS_LABELS[item.billing_status]}
                sx={{ justifySelf: "start" }}
              />
            )}
          </>
        )}
        bottomTiles={
          <>
            <BarTile>
              <TextField
                size="small"
                type="date"
                value={fromDate}
                inputProps={{ "aria-label": "С даты" }}
                sx={{ width: 150 }}
                onChange={(event) => setFromDate(event.target.value)}
              />
              <Typography variant="body2" color="text.secondary">
                —
              </Typography>
              <TextField
                size="small"
                type="date"
                value={toDate}
                inputProps={{ "aria-label": "По дату" }}
                sx={{ width: 150 }}
                onChange={(event) => setToDate(event.target.value)}
              />
            </BarTile>

            <BarTile>
              <ToggleButtonGroup
                size="small"
                value={groupBy}
                onChange={handleGroupByChange}
                aria-label="Группировка"
                sx={{
                  "& .MuiToggleButton-root": {
                    minHeight: 32,
                    paddingBlock: 0.25,
                  },
                }}
              >
                {(Object.keys(DIMENSION_LABELS) as GroupByDimension[]).map(
                  (dim) => (
                    <ToggleButton key={dim} value={dim}>
                      {DIMENSION_LABELS[dim]}
                    </ToggleButton>
                  ),
                )}
              </ToggleButtonGroup>
            </BarTile>
          </>
        }
      />

      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        {toast ? (
          <Alert severity={toast.severity} onClose={() => setToast(null)}>
            {toast.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </>
  );
};

export default ModelUsageAnalytics;
